import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, ArrowUpRight } from "lucide-react";
import {
  getBlogPostCardImage,
  getBlogPostImagePlaceholder,
  listPosts,
} from "@/content/blog";
import { BlogCardImage } from "@/components/BlogCardImage";
import { PageHero } from "@/components/PageHero";
import { CtaBand } from "@/components/sections/CtaBand";

const POSTS_PER_PAGE = 6;

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "SEO & Web Design Insights — Phil Carr Blog" },
      {
        name: "description",
        content:
          "Practical SEO, web design, and digital strategy articles for UK businesses. Notes from Phil Carr, independent SEO consultant in Yorkshire.",
      },
      { property: "og:title", content: "SEO & Web Design Insights — Phil Carr Blog" },
      {
        property: "og:description",
        content: "Practical SEO and web design articles for UK businesses.",
      },
      { property: "og:url", content: "https://www.phil-carr.co.uk/blog" },
    ],
    links: [{ rel: "canonical", href: "https://www.phil-carr.co.uk/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const posts = useMemo(() => listPosts(), []);
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(posts.length / POSTS_PER_PAGE));
  const currentPage = Math.min(page, pageCount);
  const visiblePosts = useMemo(() => {
    const start = (currentPage - 1) * POSTS_PER_PAGE;
    return posts.slice(start, start + POSTS_PER_PAGE);
  }, [currentPage, posts]);
  const firstVisible = posts.length === 0 ? 0 : (currentPage - 1) * POSTS_PER_PAGE + 1;
  const lastVisible = Math.min(currentPage * POSTS_PER_PAGE, posts.length);

  const latestDate = posts.reduce<string>((acc, p) => {
    const d = getPublishedDate(p);
    return d && !acc ? d : acc;
  }, "");

  const renderPagination = (position: "top" | "bottom") =>
    pageCount > 1 ? (
      <nav className={`blog-pagination blog-pagination-${position}`} aria-label={`News pagination ${position}`}>
        <button
          type="button"
          className="blog-pagination-button"
          disabled={currentPage === 1}
          onClick={() => {
            setPage((p) => Math.max(1, p - 1));
            document.querySelector(".blog-list")?.scrollIntoView({ behavior: "auto", block: "start" });
          }}
        >
          <ArrowLeft className="h-4 w-4" />
          Newer
        </button>
        <span className="blog-pagination-status">
          Page {currentPage} of {pageCount}
        </span>
        <button
          type="button"
          className="blog-pagination-button"
          disabled={currentPage === pageCount}
          onClick={() => {
            setPage((p) => Math.min(pageCount, p + 1));
            document.querySelector(".blog-list")?.scrollIntoView({ behavior: "auto", block: "start" });
          }}
        >
          Older
          <ArrowRight className="h-4 w-4" />
        </button>
      </nav>
    ) : null;

  return (
    <div>
      <PageHero title="Latest News & Insights 2020–2026" />

      {posts.length === 0 ? (
        <p className="container py-5 text-muted-foreground">
          Articles are being migrated from the previous site. Check back shortly.
        </p>
      ) : (
        <div className="blog-list inner-content-section container mx-auto max-w-6xl px-6 py-16 sm:px-8 lg:px-10 lg:py-24">
          <div className="blog-index-toolbar mb-8 sm:mb-10">
            <div>
              <p className="demo-section-label">SEO & website advice</p>
              <h2 className="site-heading-md mt-4 font-display font-light">
                Practical SEO notes for business owners
              </h2>
              <p className="blog-index-intro">
                Guides, project updates and plain-English advice on SEO, web design, local search and lead generation.
              </p>
            </div>
            <p className="blog-index-count m-0 text-sm text-muted-foreground">
              Showing <span className="font-semibold text-foreground">{firstVisible}-{lastVisible}</span>{" "}
              of <span className="font-semibold text-foreground">{posts.length}</span> articles
              {latestDate && (
                <>
                  <br />
                  Latest: {latestDate}
                </>
              )}
            </p>
          </div>
          {renderPagination("top")}
          <ul className="blog-grid list-unstyled pl-0">
            {visiblePosts.map((post) => (
              <li key={post.slug} className="blog-grid-item">
                <Link
                  to="/blog/$slug"
                  params={{ slug: post.slug }}
                  className="blog-card-link demo-card demo-card-hover group overflow-hidden text-decoration-none"
                >
                  <BlogCardImage
                    src={getBlogPostCardImage(post)}
                    placeholderSrc={getBlogPostImagePlaceholder(post)}
                    alt={`${getDisplayTitle(post.title)} featured image`}
                  />
                  <div className="demo-card-body flex flex-1 flex-col">
                    <div className="demo-meta-row">
                      {getPublishedDate(post) && (
                        <p className="m-0 text-sm text-muted-foreground">
                          <span className="font-semibold">Published:</span>{" "}
                          <time dateTime={post.date || undefined}>{getPublishedDate(post)}</time>
                        </p>
                      )}
                    </div>
                    <h2 className="demo-card-title font-display text-lg leading-snug">
                      {getDisplayTitle(post.title)}
                    </h2>
                    <p className="demo-card-text mt-3 line-clamp-3 min-h-[4.5rem] text-base leading-relaxed text-muted-foreground">
                      {post.description}
                    </p>
                    <span className="demo-link-arrow-button demo-card-cta mt-auto pt-6">
                      Read more
                      <span className="demo-arrow-circle">
                        <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                      </span>
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {renderPagination("bottom")}
          <details className="blog-archive-links" aria-labelledby="blog-archive-title">
            <summary className="blog-archive-summary">
              <span>
                <span className="demo-section-label">Full archive</span>
                <span id="blog-archive-title" className="blog-archive-title">
                  All SEO and web design articles
                </span>
              </span>
              <span className="blog-archive-action" aria-hidden="true">Show archive</span>
            </summary>
            <ul>
              {posts.map((post) => (
                <li key={post.slug}>
                  <Link to="/blog/$slug" params={{ slug: post.slug }}>
                    {getDisplayTitle(post.title)}
                  </Link>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}
      <CtaBand
        title="Want help applying any of this to your site?"
        body="If something here matches what you're working through, drop me a line and we'll figure out the right next step."
      />
    </div>
  );
}

function getDisplayTitle(title: string) {
  return title
    .replace(/\s*(?:\||—|-)\s*Phil Carr(?:\s+SEO Consultant)?(?:\s*&\s*Web Strategist)?\s*$/i, "")
    .trim();
}

function getPublishedDate(post: ReturnType<typeof listPosts>[number]) {
  if (post.date) {
    return new Date(post.date).toLocaleDateString("en-GB", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }
  return post.body.match(/\*\*Published:\*\*\s*([^\n]+)/i)?.[1]?.trim() ?? "";
}
